import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Popover, Typography } from '@mui/material';
import { HexColorPicker, RgbaColorPicker } from 'react-colorful';

// Colour swatch for the webshop screens. With `alpha` it edits transparency
// too and emits rgba(…) when the colour is not fully opaque — the storefront
// stores overlay / banner / arrow colours that way (the old admin's picker had
// an alpha slider). A native <input type="color"> cannot hold those: it shows
// them as black and turns them into an opaque hex on the first edit.

// Parse any color string the legacy admin might have stored. Returns RGB
// (0-255) plus alpha (0-1). Falls back to opaque white for unparseable input.
export const parseColor = (raw) => {
    if (!raw) return { r: 255, g: 255, b: 255, a: 1 };
    const str = String(raw).trim();
    if (str.toLowerCase() === 'transparent') return { r: 255, g: 255, b: 255, a: 0 };
    let m;
    if ((m = str.match(/^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)$/i))) {
        return { r: +m[1], g: +m[2], b: +m[3], a: m[4] != null ? Math.max(0, Math.min(1, +m[4])) : 1 };
    }
    if ((m = str.match(/^#([0-9a-f]{6})([0-9a-f]{2})?$/i))) {
        const h = m[1];
        return {
            r: parseInt(h.slice(0, 2), 16),
            g: parseInt(h.slice(2, 4), 16),
            b: parseInt(h.slice(4, 6), 16),
            a: m[2] ? parseInt(m[2], 16) / 255 : 1,
        };
    }
    if ((m = str.match(/^#([0-9a-f]{3})$/i))) {
        const h = m[1];
        return {
            r: parseInt(h[0] + h[0], 16),
            g: parseInt(h[1] + h[1], 16),
            b: parseInt(h[2] + h[2], 16),
            a: 1,
        };
    }
    return { r: 255, g: 255, b: 255, a: 1 };
};
// Hex output for the native <input type="color"> (which doesn't accept rgba).
export const rgbToHex = ({ r, g, b }) =>
    '#' + [r, g, b].map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('');
// Final value emitted back to the parent: rgba(…) when alpha < 1, hex when
// fully opaque. Matches the legacy admin's storage convention (it always
// wrote rgba for the size-overlay BGC/FGC, but other settings stored hex).
export const toColorString = ({ r, g, b, a }) =>
    a >= 0.999
        ? rgbToHex({ r, g, b })
        : `rgba(${r}, ${g}, ${b}, ${Math.round(a * 1000) / 1000})`;

// Tiny CSS checker pattern so the swatch shows transparency at a glance.
const CHECKER_BG = 'repeating-conic-gradient(#e7e7e7 0deg 90deg, #f6f6f6 90deg 180deg)';
const CHECKER_BG_SIZE = '8px 8px';

const ColorSwatch = ({ value, onChange, alpha = false, size = 36 }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const close = () => setAnchorEl(null);
    const parsed = parseColor(value);

    const emit = (next) => onChange(toColorString(alpha ? next : { ...next, a: 1 }));

    return (
        <>
            <Box
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                    position: 'relative', display: 'block',
                    width: size, height: size, borderRadius: 1, flexShrink: 0,
                    border: '1px solid rgba(0,0,0,0.18)',
                    cursor: 'pointer',
                    background: alpha ? CHECKER_BG : 'transparent',
                    backgroundSize: alpha ? CHECKER_BG_SIZE : undefined,
                    overflow: 'hidden',
                }}
            >
                <Box sx={{
                    position: 'absolute', inset: 0,
                    backgroundColor: alpha ? toColorString(parsed) : rgbToHex(parsed),
                }} />
            </Box>
            <Popover
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { p: 1.5 } } }}
            >
                {/* react-colorful renders saturation/value square + hue slider +
                    (when Rgba variant) alpha slider, all in one panel — no
                    OS-level dialog, no second click. Hex readout below for
                    quick verification. */}
                {alpha ? (
                    <RgbaColorPicker
                        color={parsed}
                        onChange={(c) => emit({ r: c.r, g: c.g, b: c.b, a: c.a })}
                    />
                ) : (
                    <HexColorPicker
                        color={rgbToHex(parsed)}
                        onChange={(hex) => {
                            const { r, g, b } = parseColor(hex);
                            emit({ r, g, b, a: 1 });
                        }}
                    />
                )}
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                        {alpha ? toColorString(parsed) : rgbToHex(parsed)}
                    </Typography>
                </Box>
            </Popover>
        </>
    );
};
ColorSwatch.propTypes = { value: PropTypes.string, onChange: PropTypes.func, alpha: PropTypes.bool, size: PropTypes.number };

export default ColorSwatch;
